// src/components/CreateProgram.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import axios from 'axios';
import '../styles/CreateProgram.css';

const CreateProgram = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [step, setStep] = useState(1);
    const [recentPrograms, setRecentPrograms] = useState([]);
    
    // Step 1: Program Details
    const [programDetails, setProgramDetails] = useState({
        name: '',
        description: '',
        numberOfSessions: 1
    });

    // Step 2: Session Details
    const [sessions, setSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState({
        name: '',
        order: 1,
        exercises: []
    });

    // Step 3: Exercise Entry Details
    const [currentExercise, setCurrentExercise] = useState({
        exerciseId: '',
        exerciseName: '',
        series: 'A',
        sets: 3,
        reps: 10,
        tempo: '2-0-2-0',
        rest: 60,
        notes: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(
                buildApiUrl('/programs'),
                {
                    name: programDetails.name,
                    description: programDetails.description,
                    sessions: Array(parseInt(programDetails.numberOfSessions)).fill().map((_, idx) => ({
                        name: `Session ${idx + 1}`,
                        order: idx + 1,
                        exercises: []
                    }))
                },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.data && response.data.program && response.data.program.id) {
                navigate(`/programs/${response.data.program.id}/edit`);
            }
        } catch (error) {
            console.error('Error creating program:', error);
        }
    };

    // Fetch recent programs
    useEffect(() => {
        const fetchRecentPrograms = async () => {
            try {
                const response = await axios.get(
                    buildApiUrl('/programs'),
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                const sortedPrograms = response.data
                    .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt))
                    .slice(0, 4);
                setRecentPrograms(sortedPrograms);
            } catch (error) {
                console.error('Error fetching recent programs:', error);
            }
        };

        fetchRecentPrograms();
    }, [token]);

    // Handle program deletion
    const handleDeleteProgram = async (programId) => {
        try {
            await axios.delete(
                buildApiUrl(`/programs/${programId}`),
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            // Refresh the recent programs list
            const updatedPrograms = recentPrograms.filter(program => program.id !== programId);
            setRecentPrograms(updatedPrograms);
        } catch (error) {
            console.error('Error deleting program:', error);
        }
    };

    return (
        <BaseLayout>
            <div className="create-program-container">
                {step === 1 && (
                    <div className="program-details-form">
                        <h2>Create New Program</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <h3>Program Name</h3>
                                <input
                                    type="text"
                                    value={programDetails.name}
                                    onChange={(e) => setProgramDetails({
                                        ...programDetails,
                                        name: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <h3>Description</h3>
                                <textarea
                                    value={programDetails.description}
                                    onChange={(e) => setProgramDetails({
                                        ...programDetails,
                                        description: e.target.value
                                    })}
                                />
                            </div>
                            <div className="form-group">
                                <h3>Number of Sessions</h3>
                                <input
                                    type="number"
                                    min="1"
                                    max="7"
                                    value={programDetails.numberOfSessions}
                                    onChange={(e) => setProgramDetails({
                                        ...programDetails,
                                        numberOfSessions: parseInt(e.target.value)
                                    })}
                                    required
                                />
                            </div>
                            <button type="submit">Create Program</button>
                        </form>
                    </div>
                )}

                {/* Recent Programs Section */}
                <div className="recent-programs">
                    <h2>Recent Programs</h2>
                    <div className="programs-grid">
                        {recentPrograms.map(program => (
                            <div 
                                key={program.ID} 
                                className="program-card"
                                onClick={() => navigate(`/programs/${program.ID}/edit`)}
                            >
                                <h3>{program.name}</h3>
                                <p className="description">{program.description}</p>
                                <p className="sessions-count">
                                    Sessions: {program.sessions?.length || 0}
                                </p>
                                <div className="card-footer">
                                    <button 
                                        className="edit-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/programs/${program.ID}/edit`);
                                        }}
                                    >
                                        Edit Program
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </BaseLayout>
    );
};

export default CreateProgram;
